import {computed, ref, unref} from 'vue'
import type {ComputedRef, Ref} from 'vue'
import {assetKind, mimeFromFilename, mimeMatches} from '@hc/base'
import type {AssetDto, AssetKind} from '@hc/base'
import {createGraphqlUploadTransport} from './upload_transport'
import type {UploadTransport} from './upload_transport'
import {formatBytes} from './format_bytes'

export type UploadItemStatus = 'queued' | 'uploading' | 'completing' | 'done' | 'error' | 'cancelled'

export interface UploadItem {
  id: string
  file: File
  name: string
  size: number
  mimeType: string
  kind: AssetKind
  status: UploadItemStatus
  progress: number
  error: string | null
  asset: AssetDto | null
}

export interface UseUploadOptions {
  policy: string | Ref<string>
  /** mime patterns for an early client-side check, the server stays authoritative */
  accept?: string[]
  maxSize?: number
  multiple?: boolean
  concurrency?: number
  autoStart?: boolean
  transport?: UploadTransport
}

export interface UseUpload {
  items: Ref<UploadItem[]>
  busy: ComputedRef<boolean>
  hasErrors: ComputedRef<boolean>
  add(files: File[] | FileList): UploadItem[]
  start(): Promise<AssetDto[]>
  upload(files: File[] | FileList): Promise<AssetDto[]>
  /** resets the item to queued and starts the queue */
  retry(id: string): Promise<AssetDto[]>
  cancel(id: string): void
  remove(id: string): void
  clear(): void
  reset(): void
}

let uidCounter = 0

function uid(): string {
  uidCounter++
  return `upload-${Date.now().toString(36)}-${uidCounter}`
}

// browsers report an empty type, octet-stream or (windows quirk for xlsx) application/zip for office files
const UNRELIABLE_MIME_TYPES = ['', 'application/octet-stream', 'application/zip']

function resolveMimeType(file: File): string {
  if (UNRELIABLE_MIME_TYPES.includes(file.type)) {
    const fromName = mimeFromFilename(file.name)
    if (fromName) return fromName
  }
  return file.type || 'application/octet-stream'
}

function isActive(item: UploadItem): boolean {
  return item.status === 'uploading' || item.status === 'completing'
}

interface Deferred {
  promise: Promise<void>
  resolve: () => void
}

function deferred(): Deferred {
  let resolve: () => void = () => undefined
  const promise = new Promise<void>(r => { resolve = r })
  return {promise, resolve}
}

export function useUpload(options: UseUploadOptions): UseUpload {
  const items = ref<UploadItem[]>([]) as Ref<UploadItem[]>
  const concurrency = Math.max(1, options.concurrency ?? 2)
  const multiple = options.multiple ?? true
  let transport: UploadTransport | null = options.transport || null
  const getTransport = () => transport || (transport = createGraphqlUploadTransport())

  const controllers = new Map<string, AbortController>()
  const waiters = new Map<string, Deferred>()
  let active = 0

  const busy = computed(() => items.value.some(i => i.status === 'queued' || isActive(i)))
  const hasErrors = computed(() => items.value.some(i => i.status === 'error'))

  function validate(file: File, mimeType: string): string | null {
    if (file.size === 0) return 'File is empty'
    if (options.accept && options.accept.length) {
      const svgExplicit = options.accept.some(p => p.trim().toLowerCase() === 'image/svg+xml')
      if (mimeType.toLowerCase() === 'image/svg+xml' && !svgExplicit) return 'File type not allowed'
      if (!mimeMatches(mimeType, options.accept)) return 'File type not allowed'
    }
    if (options.maxSize && file.size > options.maxSize) return `File is too large (max ${formatBytes(options.maxSize)})`
    return null
  }

  function add(files: File[] | FileList): UploadItem[] {
    const list = Array.from(files as ArrayLike<File>)
    const selected = multiple ? list : list.slice(0, 1)
    if (!multiple) {
      for (const existing of items.value.filter(isActive)) cancel(existing.id)
      items.value = []
    }
    const created: UploadItem[] = selected.map(file => {
      const mimeType = resolveMimeType(file)
      const error = validate(file, mimeType)
      return {
        id: uid(),
        file,
        name: file.name,
        size: file.size,
        mimeType,
        kind: assetKind(mimeType),
        status: error ? 'error' : 'queued',
        progress: 0,
        error,
        asset: null,
      } as UploadItem
    })
    items.value.push(...created)
    // return the reactive instances
    const result = created.map(c => items.value.find(i => i.id === c.id) as UploadItem)
    if (options.autoStart && result.some(i => i.status === 'queued')) void start()
    return result
  }

  async function process(item: UploadItem): Promise<void> {
    const controller = new AbortController()
    controllers.set(item.id, controller)
    const cancelled = () => item.status === 'cancelled'
    try {
      item.status = 'uploading'
      item.progress = 0
      item.error = null
      const t = getTransport()
      const ticket = await t.createTicket({
        policy: unref(options.policy),
        filename: item.name,
        mime_type: item.mimeType,
        size: item.size,
      })
      if (cancelled()) return
      await t.postFile(ticket, item.file, percent => {
        if (!cancelled()) item.progress = Math.max(0, Math.min(100, Math.round(percent)))
      }, controller.signal)
      if (cancelled()) return
      item.progress = 100
      item.status = 'completing'
      const asset = await t.complete(ticket.asset_id)
      if (cancelled()) return
      item.asset = asset
      item.status = 'done'
    } catch (e) {
      if (cancelled()) return
      item.status = 'error'
      item.error = `Upload failed: ${(e as Error)?.message || 'unknown error'}`
    } finally {
      controllers.delete(item.id)
    }
  }

  function pump() {
    while (active < concurrency) {
      const next = items.value.find(i => i.status === 'queued')
      if (!next) return
      next.status = 'uploading'
      active++
      process(next).finally(() => {
        active--
        const waiter = waiters.get(next.id)
        if (waiter) {
          waiters.delete(next.id)
          waiter.resolve()
        }
        pump()
      })
    }
  }

  async function start(): Promise<AssetDto[]> {
    const run = items.value.filter(i => i.status === 'queued' || isActive(i))
    const pending: Promise<void>[] = []
    for (const item of run) {
      let waiter = waiters.get(item.id)
      if (!waiter) {
        waiter = deferred()
        waiters.set(item.id, waiter)
      }
      pending.push(waiter.promise)
    }
    pump()
    await Promise.all(pending)
    return run.filter(i => i.status === 'done' && i.asset).map(i => i.asset as AssetDto)
  }

  async function upload(files: File[] | FileList): Promise<AssetDto[]> {
    const added = add(files)
    const queued = new Set(added.filter(i => i.status === 'queued').map(i => i.id))
    if (!queued.size) return []
    await start()
    return added.filter(i => queued.has(i.id) && i.status === 'done' && i.asset).map(i => i.asset as AssetDto)
  }

  function retry(id: string): Promise<AssetDto[]> {
    const item = items.value.find(i => i.id === id)
    if (!item || (item.status !== 'error' && item.status !== 'cancelled')) return Promise.resolve([])
    const error = validate(item.file, item.mimeType)
    if (error) {
      item.error = error
      item.status = 'error'
      return Promise.resolve([])
    }
    item.status = 'queued'
    item.progress = 0
    item.error = null
    item.asset = null
    return start()
  }

  function cancel(id: string) {
    const item = items.value.find(i => i.id === id)
    if (!item) return
    if (item.status === 'done' || item.status === 'error' || item.status === 'cancelled') return
    const wasQueued = item.status === 'queued'
    item.status = 'cancelled'
    const controller = controllers.get(id)
    if (controller) controller.abort()
    if (wasQueued) {
      const waiter = waiters.get(id)
      if (waiter) {
        waiters.delete(id)
        waiter.resolve()
      }
    }
  }

  function remove(id: string) {
    cancel(id)
    items.value = items.value.filter(i => i.id !== id)
  }

  function clear() {
    items.value = items.value.filter(isActive)
  }

  function reset() {
    for (const item of items.value) cancel(item.id)
    items.value = []
  }

  return {items, busy, hasErrors, add, start, upload, retry, cancel, remove, clear, reset}
}
